import { bind } from 'bind-event-listener';

import { getDocument } from '@atlaskit/browser-apis';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	ReadContentCommand,
	type ReadContentCommandResult,
} from '@atlassian/rovo-content-bridge-api-commands/commands/read-content-command';
import type { RovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api/bridge-api';
import { client } from '@atlassian/rovo-content-bridge-api/client-types';
import type { CommandHandler, CommandProperties } from '@atlassian/rovo-content-bridge-api/command';

import type { NativeEmbedChildCommand } from '../../../common/types/commands';
import {
	NativeEmbedCoreFeature,
	type NativeEmbedFeature,
} from '../../../common/types/features/main';
import { SupportedFeatures } from '../../support-feature-service/data-structures/supported-features/main';
import { NativeEmbedBridgeCommand, NativeEmbedChildBridgeCommand } from '../commands';
import {
	NativeEmbedAlwaysShowTitleChangeCommand,
	NativeEmbedClickEventCommand,
	NativeEmbedContentReadyCommand,
	NativeEmbedFeatureHandshakeCommand,
	NativeEmbedHeaderVisibilityChangeCommand,
	type NativeEmbedHeaderVisibilityState,
	NativeEmbedRequestResizeCommand,
	NativeEmbedToggleFullScreenCommand,
	NativeEmbedSelectedChangeCommand,
	type NativeEmbedViewMode,
	NativeEmbedViewModeChangeCommand,
} from '../commands/main';
import type { NativeEmbedClientId, NativeEmbedCommandParams } from '../types';

/**
 * Strategy for binding click capture. The child calls bind(handler) when
 * notify-host-click is agreed; the handler is invoked on click.
 * Returned function unbinds. Use a mock in tests to simulate clicks without DOM.
 */
export type ClickCaptureBinder = {
	bind(handler: () => void): () => void;
};

/**
 * Binder that attaches a click listener to the document. Use in production.
 */
export function createDocumentClickCaptureBinder(): ClickCaptureBinder {
	return {
		bind(handler) {
			const doc = getDocument();
			if (!doc) {
				return () => {};
			}
			const listener = () => {
				handler();
			};
			return bind(doc, {
				type: 'click',
				listener,
				options: { capture: true },
			});
		},
	};
}

/**
 * Creates a child client (production use).
 */
export function createNativeEmbedChildClient(
	bridge: RovoContentBridgeAPI,
	childClientId: NativeEmbedClientId,
	parentClientId: NativeEmbedClientId,
	supportedFeatures: Set<NativeEmbedFeature>,
): NativeEmbedChildClient {
	return new NativeEmbedChildClient(
		bridge,
		childClientId,
		parentClientId,
		new SupportedFeatures(supportedFeatures),
		createDocumentClickCaptureBinder(),
	);
}

export type OnNativeEmbedCommandCallback = (
	params: NativeEmbedCommandParams,
) => void | Promise<void>;

export type OnSelectedChangeCallback = (params: { selected: boolean }) => void | Promise<void>;

export type OnViewModeChangeCallback = (params: {
	viewMode: NativeEmbedViewMode;
}) => void | Promise<void>;

export type OnHeaderVisibilityChangeCallback = (params: {
	state: NativeEmbedHeaderVisibilityState;
}) => void | Promise<void>;

export type OnAlwaysShowTitleChangeCallback = (params: {
	alwaysShowTitle: boolean;
}) => void | Promise<void>;

export type OnReadContentCallback = CommandHandler<CommandProperties, ReadContentCommandResult>;

/**
 * Client for the native embed child.
 *
 * Responsible for:
 * - Sending and receiving commands to/from the parent.
 * - [low cohesion] Handling the feature handshake. (Could be refactored out)
 */
export class NativeEmbedChildClient {
	private bridge: RovoContentBridgeAPI;
	private childClientId: NativeEmbedClientId;
	private parentClientId: NativeEmbedClientId;
	private supportedFeatures: SupportedFeatures;
	private clickCaptureBinder: ClickCaptureBinder;
	private clickCaptureCleanup: (() => void) | null = null;
	private destroyed = false;
	private isNotifyHostClickEnabled = false;
	private hasPendingClickBeforeFeatureAgreement = false;
	private onSelectedChangeCallback: OnSelectedChangeCallback | null = null;
	private onHeaderVisibilityChangeCallback: OnHeaderVisibilityChangeCallback | null = null;
	private onAlwaysShowTitleChangeCallback: OnAlwaysShowTitleChangeCallback | null = null;
	private onReadContentCallback: OnReadContentCallback | null = null;

	constructor(
		bridge: RovoContentBridgeAPI,
		childClientId: NativeEmbedClientId,
		parentClientId: NativeEmbedClientId,
		supportedFeatures: SupportedFeatures,
		clickCaptureBinder: ClickCaptureBinder,
	) {
		this.bridge = bridge;
		this.childClientId = childClientId;
		this.parentClientId = parentClientId;
		this.supportedFeatures = supportedFeatures;
		this.clickCaptureBinder = clickCaptureBinder;

		this.setupFeatureHandshakeListener();
		this.setupClickCaptureWhenEnabled();
		this.setupSelectedChangeListener();
		this.setupHeaderVisibilityChangeListener();
		this.setupAlwaysShowTitleChangeListener();
		this.setupReadContentListener();

		// Register as ready before sending the handshake so the bridge can deliver the message.
		this.bridge.ready(client(this.childClientId));
		this.sendFeatureHandshake();
	}

	/**
	 * Single handler for selected-change: runs any user callback from onSelectedChange.
	 * We register once so that calling onSelectedChange does not overwrite the
	 * internal handler (bridges typically allow one handler per command).
	 */
	private setupSelectedChangeListener() {
		this.bridge.on(this.childClientId, NativeEmbedSelectedChangeCommand, (params) => {
			this.onSelectedChangeCallback?.({ selected: params.selected });
		});
	}

	/**
	 * Single handler for header-visibility-change: runs any user callback from
	 * onHeaderVisibilityChange.
	 */
	private setupHeaderVisibilityChangeListener() {
		this.bridge.on(this.childClientId, NativeEmbedHeaderVisibilityChangeCommand, (params) => {
			this.onHeaderVisibilityChangeCallback?.({ state: params.state });
		});
	}

	/**
	 * Single handler for always-show-title-change: runs any user callback from
	 * onAlwaysShowTitleChange. Registered in the constructor so the bridge handler
	 * is in place before the handshake completes, preventing dropped messages.
	 */
	private setupAlwaysShowTitleChangeListener() {
		this.bridge.on(this.childClientId, NativeEmbedAlwaysShowTitleChangeCommand, (params) => {
			this.onAlwaysShowTitleChangeCallback?.({ alwaysShowTitle: params.alwaysShowTitle });
		});
	}

	private setupFeatureHandshakeListener(): void {
		this.bridge.on(this.childClientId, NativeEmbedFeatureHandshakeCommand, (params) => {
			this.supportedFeatures.completeWithAgreedFeatures(new Set(params.features));
		});
	}

	private setupReadContentListener(): void {
		if (!fg('maui_remixable_page_native_embeds')) {
			return;
		}

		this.bridge.on(this.childClientId, ReadContentCommand, (params) => {
			return this.onReadContentCallback?.(params);
		});
	}

	private sendFeatureHandshake(): void {
		this.bridge.send(NativeEmbedFeatureHandshakeCommand, {
			senderId: this.childClientId,
			receiverId: this.parentClientId,
			features: Array.from(this.supportedFeatures.getLocalSupportedFeatures()),
		});
	}

	private sendClickEventToHost(): void {
		this.bridge.send(NativeEmbedClickEventCommand, {
			senderId: this.childClientId,
			receiverId: this.parentClientId,
		});
	}

	private onPendingReplayFeaturesResolved(features: Set<NativeEmbedFeature>): void {
		if (this.destroyed) {
			return;
		}
		if (!features.has(NativeEmbedCoreFeature.NotifyHostClick)) {
			this.hasPendingClickBeforeFeatureAgreement = false;
			this.clickCaptureCleanup?.();
			return;
		}
		this.isNotifyHostClickEnabled = true;
		if (this.hasPendingClickBeforeFeatureAgreement) {
			this.hasPendingClickBeforeFeatureAgreement = false;
			this.sendClickEventToHost();
		}
	}
	/**
	 * When agreed features include notify-host-click, capture click events
	 * and send them to the parent so the host can select this embed.
	 */
	private setupClickCaptureWhenEnabled(): void {
		const handler = () => {
			if (this.destroyed) {
				return;
			}
			if (this.isNotifyHostClickEnabled) {
				this.sendClickEventToHost();
				return;
			}
			this.hasPendingClickBeforeFeatureAgreement = true;
		};
		const unbind = this.clickCaptureBinder.bind(handler);
		this.clickCaptureCleanup = () => {
			unbind();
			this.clickCaptureCleanup = null;
		};

		this.supportedFeatures.getFeaturesPromise().then((features) => {
			this.onPendingReplayFeaturesResolved(features);
		});
	}

	onNativeEmbedCommand(handler: OnNativeEmbedCommandCallback): void {
		this.bridge.on(this.childClientId, NativeEmbedBridgeCommand, handler);
	}

	/**
	 * Subscribe to selected-change events from the parent.
	 * Only emitted when the SelectedChange feature is agreed in the handshake.
	 */
	onSelectedChange(handler: OnSelectedChangeCallback): void {
		this.onSelectedChangeCallback = handler;
	}

	/**
	 * Subscribe to view-mode-change events from the parent.
	 * Only emitted when the ViewModeChange feature is agreed in the handshake.
	 */
	onViewModeChange(handler: OnViewModeChangeCallback): void {
		this.bridge.on(this.childClientId, NativeEmbedViewModeChangeCommand, (params) =>
			handler({ viewMode: params.viewMode }),
		);
	}

	/**
	 * Subscribe to header-visibility-change events from the parent.
	 * Only emitted when the HeaderVisibilityChange feature is agreed in the handshake.
	 * Calling this multiple times replaces the previous handler.
	 */
	onHeaderVisibilityChange(handler: OnHeaderVisibilityChangeCallback): void {
		this.onHeaderVisibilityChangeCallback = handler;
	}

	/**
	 * Subscribe to always-show-title-change events from the parent.
	 * Emitted when the user toggles the always-show-title option.
	 */
	onAlwaysShowTitleChange(handler: OnAlwaysShowTitleChangeCallback): void {
		this.onAlwaysShowTitleChangeCallback = handler;
	}

	/**
	 * Subscribe to read-content events from the parent.
	 * Emitted when the user reads the content of the embed like when remixing the embed.
	 */
	onReadContent(handler: OnReadContentCallback): void {
		this.onReadContentCallback = handler;
	}

	sendChildCommand(command: NativeEmbedChildCommand): void {
		this.bridge.send(NativeEmbedChildBridgeCommand, {
			senderId: this.childClientId,
			receiverId: this.parentClientId,
			...command,
		});
	}

	/**
	 * Request the host to toggle the full-screen dialog.
	 * Only effective when the ToggleFullScreen feature is agreed in the handshake.
	 */
	sendToggleFullScreen(): void {
		this.bridge.send(NativeEmbedToggleFullScreenCommand, {
			senderId: this.childClientId,
			receiverId: this.parentClientId,
		});
	}

	/**
	 * Request the host to resize the embed container to the given pixel dimensions.
	 *
	 * When `options.local` is `true` the host treats the request as an
	 * ephemeral, in-memory size override and MUST NOT persist the dimensions
	 * to the ADF node parameters.
	 * Only effective when the RequestResize feature is agreed in the handshake.
	 */
	sendRequestResize(width?: number, height?: number, options?: { local?: boolean }): void {
		this.bridge.send(NativeEmbedRequestResizeCommand, {
			senderId: this.childClientId,
			receiverId: this.parentClientId,
			width,
			height,
			local: options?.local,
		});
	}

	/**
	 * Signal the host that this embed's content is fully rendered and visible.
	 *
	 * Call this once the initial data has loaded and the first meaningful frame
	 * has been painted. The host uses it to dismiss its loading overlay, hiding
	 * any internal loading state that would otherwise flash between the iframe
	 * `load` event and this first render.
	 */
	sendContentReady(): void {
		this.bridge.send(NativeEmbedContentReadyCommand, {
			senderId: this.childClientId,
			receiverId: this.parentClientId,
		});
	}

	nativeEmbedFeatures(): Promise<Set<NativeEmbedFeature>> {
		return this.supportedFeatures.getFeaturesPromise();
	}

	/**
	 * Removes click capture listeners.
	 * Call when the child client is no longer needed.
	 */
	destroy(): void {
		this.destroyed = true;
		this.isNotifyHostClickEnabled = false;
		this.hasPendingClickBeforeFeatureAgreement = false;
		this.clickCaptureCleanup?.();
		this.bridge.teardown(client(this.childClientId));
	}
}
